import React, {useEffect, useState, useRef} from "react";
import "./EventStream.css";


import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import LiveStream from "./LiveStream";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEye, faEyeSlash, faPlay, faStop} from "@fortawesome/free-solid-svg-icons";

import BondEvents from "../Bond/BondEvents/index.jsx";
import UserEvents from "../UserEvents/index.jsx";
import HomeSelector from "../HomeSelector/index.jsx";


function EventStream({bondIds, bondsData, userId, homeMembers, homes, selectedHomeId, onSelectHome, onRefresh, onHomeChange}) {
    const allBondIds = bondIds || [];

    const [events, setEvents] = useState([]);
    const [eventsByRequestId, setEventsByRequestId] = useState({});
    const [visibleBonds, setVisibleBonds] = useState({});
    const [, forceUpdate] = useState(0);
    const bondEventsRefs = useRef({});
    const userEventsRef = useRef(null);
    const prevBondIdsRef = useRef();
    const prevUserIdRef = useRef();

    // Clear events when the bond set changes. A USER change clears everything;
    // a HOME switch (same user, different bonds) must keep the user-stream events —
    // UserEvents never remounts for the same userId, so a cleared user stream would
    // never reload (useStreamEvents only fetches once per clientId).
    useEffect(() => {
        const prevBondIds = prevBondIdsRef.current;
        const currentBondIds = bondIds?.join(',') || '';
        const userChanged = prevUserIdRef.current !== undefined && prevUserIdRef.current !== userId;

        if (prevBondIds && prevBondIds !== currentBondIds) {
            if (userChanged) {
                // User changed - clear all events and state
                setEvents([]);
                setEventsByRequestId({});

                // Clear module-level caches to allow reloading for new user
                if (window.streamEventsLoaded) {
                    Object.keys(window.streamEventsLoaded).forEach(key => {
                        delete window.streamEventsLoaded[key];
                    });
                }
            } else {
                // Home switch - drop only bond-stream events, keep the user stream
                setEvents(prev => prev.filter(e => e.source === 'user'));
                setEventsByRequestId(prev => {
                    const next = {};
                    for (const [reqId, evts] of Object.entries(prev)) {
                        const kept = evts.filter(e => e.source === 'user');
                        if (kept.length > 0) next[reqId] = kept;
                    }
                    return next;
                });

                // Reset loaded-flags for bond streams only, so the new home's bonds reload
                if (window.streamEventsLoaded) {
                    Object.keys(window.streamEventsLoaded).forEach(key => {
                        if (key !== userId) delete window.streamEventsLoaded[key];
                    });
                }
            }
            setVisibleBonds({});
        }

        prevBondIdsRef.current = currentBondIds;
        prevUserIdRef.current = userId;
    }, [bondIds, userId]);

    // Initialize all bonds as visible
    useEffect(() => {
        if (bondIds && bondIds.length > 0) {
            const initialVisible = {};
            bondIds.forEach(bondId => {
                if (visibleBonds[bondId] === undefined) {
                    initialVisible[bondId] = true;
                }
            });
            if (Object.keys(initialVisible).length > 0) {
                setVisibleBonds(prev => ({...prev, ...initialVisible}));
            }
        }
    }, [bondIds]);

    // Periodic update to sync button states
    useEffect(() => {
        const interval = setInterval(() => {
            forceUpdate(n => n + 1);
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // Filter events based on the active bond set and per-bond visibility.
    // Bond-tagged events — including user-stream ones like INTEGRATIONS EXEC, which
    // carry attributes.bond_id — only show when their bond belongs to the currently
    // selected home's bonds (or the user's own bonds when no home is selected).
    const activeBondSet = new Set(allBondIds);
    const filteredEvents = events.filter(event => {
        const eventBondId = event.bond_id;
        if (!eventBondId) return true; // Show events without bond_id (e.g. HOME events)
        // Alexa SYNC events carry a list of bond ids — show on any overlap
        if (Array.isArray(eventBondId)) return eventBondId.some(id => activeBondSet.has(id));
        if (!activeBondSet.has(eventBondId)) return false; // belongs to another home
        return visibleBonds[eventBondId] !== false;
    });

    const handleToggleBondVisibility = (bondId, isVisible) => {
        setVisibleBonds(prev => ({
            ...prev, [bondId]: isVisible
        }));
    };

    // Check if all bonds + user are visible
    const allBondsVisible = allBondIds.every(bondId => visibleBonds[bondId] !== false)
        && (!userId || visibleBonds['__user__'] !== false);

    // Toggle all bonds + user visibility
    const handleToggleAllBonds = () => {
        allBondIds.forEach(bondId => {
            const ref = bondEventsRefs.current[bondId];
            if (ref && ref.isVisible && ref.toggleVisibility) {
                if (ref.isVisible() === allBondsVisible) {
                    ref.toggleVisibility();
                }
            }
        });
        if (userId && userEventsRef.current?.isVisible && userEventsRef.current?.toggleVisibility) {
            if (userEventsRef.current.isVisible() === allBondsVisible) {
                userEventsRef.current.toggleVisibility();
            }
        }
    };

    // Start/Stop all bonds + user
    const handleStartStopAll = () => {
        const anyConnected = allBondIds.some(bondId => {
            const ref = bondEventsRefs.current[bondId];
            return ref && ref.isConnected && ref.isConnected();
        }) || !!(userEventsRef.current?.isConnected && userEventsRef.current.isConnected());

        allBondIds.forEach(bondId => {
            const ref = bondEventsRefs.current[bondId];
            if (ref) {
                if (anyConnected && ref.disconnect) ref.disconnect();
                else if (!anyConnected && ref.connect) ref.connect();
            }
        });

        if (userEventsRef.current) {
            if (anyConnected && userEventsRef.current.disconnect) userEventsRef.current.disconnect();
            else if (!anyConnected && userEventsRef.current.connect) userEventsRef.current.connect();
        }

        setTimeout(() => forceUpdate(n => n + 1), 100);
    };

    // Check if any bonds or user are connected
    const anyBondsConnected = allBondIds.some(bondId => {
        const ref = bondEventsRefs.current[bondId];
        return ref && ref.isConnected && ref.isConnected();
    }) || !!(userEventsRef.current?.isConnected && userEventsRef.current.isConnected());

    // Handle date range filter
    const handleDateRangeFilter = async (startDate, endDate, limit = 100) => {
        setEvents([]);
        setEventsByRequestId({});

        const loadPromises = [];

        if (bondIds) {
            bondIds.forEach(bondId => {
                if (visibleBonds[bondId] === false) return;
                const ref = bondEventsRefs.current[bondId];
                if (ref && ref.loadEventsWithDateRange) {
                    loadPromises.push(ref.loadEventsWithDateRange(startDate, endDate, limit));
                }
            });
        }

        if (userId && userEventsRef.current?.loadEventsWithDateRange) {
            loadPromises.push(userEventsRef.current.loadEventsWithDateRange(startDate, endDate, limit));
        }

        await Promise.all(loadPromises);
    };

    return (<Row>
        <HomeSelector homes={homes} selectedHomeId={selectedHomeId} onSelectHome={onSelectHome} />
        <div className={"d-flex justify-content-between align-items-start gap-2 mb-3"}>
            {/* Bond Events on the left */}
            <div className={"d-flex flex-wrap gap-2"}>
                {userId && (
                    <UserEvents
                        ref={userEventsRef}
                        userId={userId}
                        setEvents={setEvents}
                        setEventsByRequestId={setEventsByRequestId}
                        onToggleVisibility={handleToggleBondVisibility}
                        onRefresh={onRefresh}
                        onHomeChange={onHomeChange}
                    />
                )}
                {allBondIds.map((bondId) => {
                    const bondData = bondsData[bondId];
                    const connectivity = bondData?.connectivity || {};

                    return (<BondEvents
                        key={bondId}
                        ref={(el) => (bondEventsRefs.current[bondId] = el)}
                        bondId={bondId}
                        events={events}
                        setEvents={setEvents}
                        eventsByRequestId={eventsByRequestId}
                        setEventsByRequestId={setEventsByRequestId}
                        onToggleVisibility={handleToggleBondVisibility}
                        bondConnectivity={connectivity}
                        isMate={bondId.startsWith('SM')}
                    />);
                })}
            </div>

            {/* Control buttons on the right */}
            {(allBondIds.length + (userId ? 1 : 0)) > 1 && (
                <div className={"d-flex gap-2 flex-shrink-0"}>
                    {/* Start/Stop All Button */}
                    <Button
                        size="sm"
                        variant={anyBondsConnected ? "danger" : "outline-success"}
                        onClick={handleStartStopAll}
                        className="p-0 d-flex align-items-center justify-content-center"
                        style={{width: '32px', height: '32px', minWidth: '32px'}}
                        title={anyBondsConnected ? 'Stop all live streams' : 'Start all live streams'}
                    >
                        <FontAwesomeIcon icon={anyBondsConnected ? faStop : faPlay} size="sm" />
                    </Button>

                    {/* Show/Hide All Button */}
                    <Button
                        size="sm"
                        variant={allBondsVisible ? "outline-primary" : "outline-secondary"}
                        onClick={handleToggleAllBonds}
                        className="p-0 d-flex align-items-center justify-content-center"
                        style={{width: '32px', height: '32px', minWidth: '32px'}}
                        title={allBondsVisible ? 'Hide all bond events' : 'Show all bond events'}
                    >
                        <FontAwesomeIcon icon={allBondsVisible ? faEye : faEyeSlash} size="sm" />
                    </Button>
                </div>
            )}
        </div>
        <LiveStream
            events={filteredEvents}
            eventsByRequestId={eventsByRequestId}
            bondsData={bondsData}
            bondIds={bondIds}
            homeMembers={homeMembers}
            homes={homes}
            userId={userId}
            onDateRangeFilter={handleDateRangeFilter}
        />
    </Row>);
}

export default EventStream;
